module Meevo.Plugins.MeevoMessengerSettings {
    'use strict';

    import IMeevoMessengerSettings = Meevo.Model.IMeevoMessengerSettings;
    import IMeevoMessengerAutoReply = Meevo.Model.IMeevoMessengerAutoReply;
    import MeevoMessengerAutoRepliesEnum = Meevo.Model.MeevoMessengerAutoRepliesEnum;
    import MeevoMessengerMessages = Meevo.Plugins.MeevoMessenger.MeevoMessengerMessages;

    export interface IMeevoMessengerSettingsTextelSection extends Meevo.Components.CommonClasses.ICommonSection {
        edit: () => void,
        initializeHeader: () => void,
        initialSetup: () => void
        deletePhoneNumber: (callback: Function) => void
    }

    export class TextelSection extends Meevo.Components.CommonClasses.CommonSectionBase implements IMeevoMessengerSettingsTextelSection {
        public params: ISectionParamsMeevoMessengerSettings;
        public model: Partial<IMeevoMessengerSettings>;
        public employeesCriteria = {
            showEmployeesOnly: true,
            includeActive: true,
            includeInactive: true,
            iIncludeDeleted: true
        };

        public isEditingMessaging: boolean = false;
        public countryCode: string = '1';
        public autoReplies: IMeevoMessengerAutoReply[] = [
            { type: MeevoMessengerAutoRepliesEnum.DefaultAutoReplyMessage, name: 'LABEL_AWAY_FROM_DESK', editingLabel: 'LABEL_MEEVO_MESSENGER_DEFAULT_AUTO_REPLY_MESSAGE', default: 'TEXT_MEEVO_MESSENGER_DEFAULT_AUTOREPLY_MESSAGE' },
            { type: MeevoMessengerAutoRepliesEnum.HolidayAutoReplyMessage, name: 'LABEL_CLOSED_FOR_HOLIDAY', editingLabel: 'LABEL_MEEVO_MESSENGER_HOLIDAY_AUTO_REPLY_MESSAGE', default: 'TEXT_MEEVO_MESSENGER_CLOSED_FOR_HOLIDAY_MESSAGE' },
            { type: MeevoMessengerAutoRepliesEnum.OutsideHoursAutoReplyMessage, name: 'LABEL_MEEVO_MESSENGER_OUTSIDE_HOURS', editingLabel: 'LABEL_MEEVO_MESSENGER_OUTSIDE_HOURS_AUTO_REPLY_MESSAGE', default: 'TEXT_MEEVO_MESSENGER_AFTER_HOURS_MESSAGE' }
        ];
        private classification: string = 'Textel';

        constructor(
            params: ISectionParamsMeevoMessengerSettings,
            protected $filter: angular.IFilterService,
            protected $translate: angular.translate.ITranslateService,
            protected meevoMessengerSettingsService: IMeevoMessengerSettingsService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected $scope: angular.IScope,
            protected $q: angular.IQService,
            protected $rootScope: angular.IRootScopeService
        ) {
            super(params.sectionId,
                params.displayNameContentKey,
                params.template,
                params.isPrimarySection,
                params.handlesOwnCrud,
                params.baseEndpoint,
                params.sectionEndpoint,
                params.loggingService,
                params.dataService,
                params.noun,
                params.identityService,
                params.sectionSecurityKey);

            this.params = params;

            // Need for updating Entity Version from meevoMessenger data
            // initializeHeader() is not imported to execute here, because data in Textel don't change
            this.$scope.$on(Meevo.Components.Common.Messages.TEXTEL_SETTINGS_UPDATE_MODEL, (event: angular.IAngularEvent, settings: IMeevoMessengerSettings) => this.model = settings);
        }

        initializeHeader() {
            this.model.quickAnswers = this.$filter('orderBy')(this.model.quickAnswers, 'orderNumber');
            this.autoPopulateQuickReply();

            this.autoReplies = this.autoReplies.map((autoReply: IMeevoMessengerAutoReply) => {
                Object.defineProperty(autoReply, 'body', {
                    get: () => { return this.model[this.autoReplyPropertyName(autoReply.type)]; },
                    set: (newValue: string) => { this.model[this.autoReplyPropertyName(autoReply.type)] = newValue; }
                });
                return autoReply;
            });
        }

        initialSetup() {
            this.params.busyService.start();
            this.meevoMessengerSettingsService.initialSetup(this.classification)
                .then((settings: IMeevoMessengerSettings) => {
                    if (!settings.isAccountInitialized) {
                        this.modalService.error('MESSAGE_MEEVO_MESSENGER_INITIAL_SETUP_ERROR');
                        return;
                    }
                    _.assign(this.model, settings);
                    this.model.classification = 'TEXTEL';
                    this.modalService.alert('MESSAGE_MEEVO_MESSENGER_INITIAL_SETUP_SUCCESS');
                }, error => {
                    this.modalService.error('MESSAGE_MEEVO_MESSENGER_INITIAL_SETUP_ERROR');
                }).finally(() => {
                    this.params.busyService.stop();
                });
        }

        deletePhoneNumber(callback: Function) {
            this.modalService.confirm('LABEL_DELETE_ACCOUNT_CONFIRMATION')
                .then(res => this.deleteAccountRequest())
                .then(() => {
                    this.model = this.defaultModel;
                    this.autoPopulateQuickReply();
                    this.takeBackup();
                    this.clearTextelSettingsAndAuth();
                    setTimeout(() => { this.model.userName = "" }, 0)
                    callback();
                })
                .catch(err => {})
        }

        preSave(): void {
            this.isBusinessHour();
            this.autoPopulateQuickReply();
            this.endEditingMessaging();
            // Categories ids from the smart mover
            delete this.model.userCategoriesIdsList;
        }

        isBusinessHour() {
            if (this.model.useBusinessHours) {
                this.model.isAutoReplyEnabled = false;
            }
        }

        loginAuthorization(userName: string, token: string): ng.IPromise<any> {
            return this.meevoMessengerSettingsService.meevoMessengerLogin({ userName, token, classification: 'Textel'});
        }

        deleteQuickResponse(index: number): void {
            this.model.quickAnswers.splice(index, 1);
        }

        edit() {
            this.startEditingMessaging();
        }

        rollbackChanges() {
            super.rollbackChanges();
            this.endEditingMessaging();
        }

        private clearTextelSettingsAndAuth(): void {
            this.$rootScope.$broadcast(MeevoMessengerMessages.CLEAR_TEXTEL_SETTINGS_AND_AUTH)
        }

        private deleteAccountRequest(): any {
            this.params.busyService.start();
            this.meevoMessengerSettingsService.deleteAccount()
                .then(() => this.$q.resolve({}))
                .finally(() => {this.params.busyService.stop()})
        }

        private autoPopulateQuickReply(): void {
            this.autoReplies.forEach(autoReply => {
                let propertyName = this.autoReplyPropertyName(autoReply.type);
                if (!this.model[propertyName]) {
                    this.model[propertyName] = this.$translate.instant(autoReply.default);
                }
            });
        }

        private autoReplyPropertyName(type: MeevoMessengerAutoRepliesEnum): string {
            let propertyName = MeevoMessengerAutoRepliesEnum[type];
            // Set first letter to lower case
            return propertyName.charAt(0).toLowerCase() + propertyName.substr(1);
        }

        private startEditingMessaging(): void {
            this.isEditingMessaging = true;
        }

        private endEditingMessaging(): void {
            this.isEditingMessaging = false;
        }

        private isShowSuccessfullLogin(meevoMessengerLoginAuth: any): boolean {
            return !meevoMessengerLoginAuth.$error.credentialsVerifyError && meevoMessengerLoginAuth.$submitted && !meevoMessengerLoginAuth.$pending;
        }

        private isShowAuthorizationFailed(meevoMessengerLoginAuth: any): boolean {
            return meevoMessengerLoginAuth.$error.credentialsVerifyError && meevoMessengerLoginAuth.$submitted && !meevoMessengerLoginAuth.$pending;
        }

        private isValidMeevoMessengerAuthFields(meevoMessengerLoginAuth: any): boolean {
            return meevoMessengerLoginAuth.meevoMessengerUserName.$valid && meevoMessengerLoginAuth.meevoMessengerToken.$valid;
        }

        get defaultModel() {
            return {
                isAutoReplyEnabled: false,
                autoReplyKind: 0,
                defaultAutoReplyMessage:"",
                entityVersion: 0,
                holidayAutoReplyMessage: "",
                isAccountInitialized: false,
                locationId: 0,
                outsideHoursAutoReplyMessage: "",
                quickAnswers: [],
                tenantId: 0,
                useBusinessHours: false,
                userCategoriesIdsList: [],
                userName: "",
                token: "",
                usersToNotify: []
            }
        }

        get securityAccessEnum() {
            return Meevo.Model.SecurityAccessEnum;
        }

        get messageLengthLimit(): number {
            return Meevo.Plugins.MeevoMessenger.Constants.MESSAGE_SMS_LENGTH_LIMIT
        }
    }
}
